import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
  static targets = ["input", "preview", "progress"]
  static values = {
    url: String,
    multiple: Boolean
  }

  connect() {
    this.bindEvents()
    this.currentUploads = new Set()
    
    // Setup file input change handler
    if (this.hasInputTarget) {
      this.inputTarget.addEventListener("change", this.handleFileInputChange.bind(this))
    }
  }

  bindEvents() {
    ["dragenter", "dragover", "dragleave", "drop"].forEach(eventName => {
      this.element.addEventListener(eventName, this.preventDefaults, false)
    })

    this.element.addEventListener("dragenter", this.highlight.bind(this))
    this.element.addEventListener("dragover", this.highlight.bind(this))
    this.element.addEventListener("dragleave", this.unhighlight.bind(this))
    this.element.addEventListener("drop", this.handleDrop.bind(this))
  }

  preventDefaults(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  highlight() {
    this.element.classList.add("border-indigo-500", "ring-2", "ring-indigo-500", "ring-opacity-50")
  }

  unhighlight() {
    this.element.classList.remove("border-indigo-500", "ring-2", "ring-indigo-500", "ring-opacity-50")
  }

  handleDrop(e) {
    const dt = e.dataTransfer
    const files = dt.files
    this.unhighlight()
    this.uploadFiles(files)
  }
  
  handleFileInputChange(e) {
    const files = e.target.files
    if (files && files.length > 0) {
      this.uploadFiles(files)
    }
  }

  uploadFiles(files) {
    Array.from(files).forEach(file => {
      // Only process images
      if (!file.type.match(/^image\//)) return

      const upload = new DirectUpload(file, this.urlValue, {
        directUploadWillStoreFileWithXHR: (request) => {
          request.upload.addEventListener("progress", event => this.uploadRequestDidProgress(event))
        }
      })

      this.currentUploads.add(upload)
      this.dispatch("uploadStarted")
      
      // Store the original file for preview
      const originalFile = file
      
      upload.create((error, blob) => {
        if (error) {
          console.error(error)
        } else {
          // Add the blob ID to the file input
          const hiddenField = document.createElement('input')
          hiddenField.type = 'hidden'
          hiddenField.name = this.inputTarget.name
          hiddenField.value = blob.signed_id
          this.element.appendChild(hiddenField)

          // Create preview using the original file
          this.createPreview(originalFile)
        }
        this.currentUploads.delete(upload)
        this.dispatch("uploadFinished")
      })
    })
  }

  uploadRequestDidProgress(event) {
    const progress = event.loaded / event.total * 100
    if (this.hasProgressTarget) {
      this.progressTarget.style.width = `${progress}%`
    }
    this.dispatch("uploadProgressed", { detail: { progress } })
  }

  createPreview(blob) {
    const img = document.createElement('img')
    
    // Check if blob is a File object (from drag and drop) or a blob object (from ActiveStorage)
    if (blob instanceof File) {
      img.src = URL.createObjectURL(blob)
    } else if (blob.preview_url) {
      // Use the preview URL provided by ActiveStorage
      img.src = blob.preview_url
    } else if (blob.signed_id) {
      // If we have a signed_id but no preview URL, use a placeholder
      img.src = '/assets/placeholder-image.png'
    } else {
      console.error('Invalid blob object:', blob)
      return // Exit if we can't create a preview
    }
    
    img.classList.add('h-24', 'w-full', 'object-cover', 'rounded-lg')

    const wrapper = document.createElement('div')
    wrapper.className = 'relative group'
    wrapper.appendChild(img)

    // Add remove button
    const removeButton = document.createElement('button')
    removeButton.type = 'button'
    removeButton.className = 'absolute top-2 right-2 p-1 bg-red-500 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity'
    
    // Add X icon
    const icon = document.createElement('i')
    icon.className = 'ph ph-x text-white'
    removeButton.appendChild(icon)
    
    // Add event listener to remove
    removeButton.addEventListener('click', () => {
      wrapper.remove()
      // TODO: Remove the corresponding hidden input if needed
    })
    
    wrapper.appendChild(removeButton)
    
    // Add to preview container
    if (this.hasPreviewTarget) {
      this.previewTarget.appendChild(wrapper)
    }
  }
}
